<template>
  <div class="new-item">
    <h3 class="text-center">create new enterprise</h3>
    
    <Loading v-if="httpLoadingBusy"/>
    
    <VeeForm v-slot="{ invalid }">
      <form autocomplete="off" v-if="!httpLoadingBusy">

        <div class="form-group">
          <label for="enterpriseId">enterprise id</label>
          <Field
            name="enterpriseId" 
            :rules="{
              required:true, 
              validNewEntepriseId:true, 
              possibleEnterpriseId: {curEntIdList: entIdList} 
            }" 
            v-slot="{ field, errors }"
          >
            <input type="text" class="form-control" name="enterpriseId" placeholder="nnn.client ..." v-bind="field" v-model="enterpriseId">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <div class="custom-control custom-switch">
            <input type="checkbox" class="custom-control-input" id="dataStorage" v-model="dataStorage">
            <label class="custom-control-label" for="dataStorage">storage enabled</label>
          </div>
        </div>

        <div class="form-group">
          <div class="custom-control custom-switch">
            <input type="checkbox" class="custom-control-input" id="dataDecoding" v-model="dataDecoding">
            <label class="custom-control-label" for="dataDecoding">decoding enabled</label>
          </div>
        </div>

        <div class="form-group">
          <label for="enrolProfile">device enrol profile</label>
          <select class="form-control" v-model="enrolId">
            <option v-for="enrolProfileId of enrolProfileIdList" :key="enrolProfileId" :value="enrolProfileId">{{enrolProfileId}}</option>
          </select>
        </div>

        <div class="form-group">
          <label for="subscriberId">actility subscriber id</label>
          <Field name="subscriberId" rules="required|validSubscriberId" v-slot="{ field, errors }">
            <input type="number" class="form-control" name="subscriberId" placeholder="i.e. 1100000000..." v-bind="field" v-model="subscriberId">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="supplierId">actility supplier id</label>
          <Field name="supplierId" rules="validSupplierId" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="supplierId" v-bind="field" v-model="supplierId">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <button 
          class="btn btn-info new-device-btn"
          type="submit" 
          @click.prevent="doCreate()" 
          :disabled="invalid || !canCreate"
>
          <font-awesome-icon icon="plus"/> create enterprise</button>

        <button type="button" class="btn btn-secondary" @click="cancel()">Cancel</button>

      </form>
    </VeeForm>

    <Error :label="error"/>
    <Success :label="success"/>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import Loading from '@/components/Loading.vue';
import Error from '@/components/Error.vue';
import Success from '@/components/Success.vue';
import { ApiService, curApiService } from '@/services/ApiService';
import { ApiClient, curApiClient } from '@/services/ApiClient';

interface Props {
  enrolProfileIdList: string[];
}

withDefaults(defineProps<Props>(), {
  enrolProfileIdList: () => []
});

const emit = defineEmits<{
  cancel: [];
}>();

const router = useRouter();

// our model
const error = ref<string>('');
const success = ref<string>('');

// form data
const enterpriseId = ref<string>('');
const dataStorage = ref<boolean>(true);
const dataDecoding = ref<boolean>(true);
const enrolId = ref<string>('default');
const subscriberId = ref<number>(1100000000);
const supplierId = ref<string>('');

const httpLoadingBusy = ref<boolean>(false);
const httpCreatingBusy = ref<boolean>(false);

const entIdList = ref<string[]>([]);

// internal dependencies
const apiService: ApiService = curApiService;
const apiClient: ApiClient = curApiClient;

onMounted(async () => {
  httpLoadingBusy.value = true;

  const enterprisesReply = await apiService.getEnterprises({});

  httpLoadingBusy.value = false;
  
  if(enterprisesReply.httpCode === 403)
    return router.push({name:'login'});

  if(enterprisesReply.errorMsg)
    return error.value = `Unable to retrieve enterprise list: ${enterprisesReply.errorMsg}`;
  
  // we update the state
  entIdList.value = enterprisesReply.entList!.map(ent => ent.enterpriseId);
});

const canCreate = computed<boolean>(() => {
  return !httpLoadingBusy.value && !httpCreatingBusy.value;
});

const doCreate = async () => {
  httpCreatingBusy.value = true;
  error.value = '';

  const newEnterpriseReq = {
    enterpriseId: enterpriseId.value,
    dataDecoding: dataDecoding.value,
    dataStorage: dataStorage.value,
    nwServer: {
      actility: {
        subscriberId: subscriberId.value,
        supplierId: supplierId.value !== '' ? supplierId.value : undefined,
        enrolId: enrolId.value
      }
    }
  };

  const reply = await apiClient.createEnterprise(newEnterpriseReq);

  httpCreatingBusy.value = false;
  
  if(reply.errorMsg)
    return error.value = `unable to create enterprise: ${reply.errorMsg}`;

  setSuccess(`enterprise ${enterpriseId.value} created successfully`);
};

const setSuccess = (str: string) => {
  success.value = str;
  setTimeout(() => emit('cancel'), 2000);
};

const cancel = () => {
  emit('cancel');
};
</script>

<style scoped lang="scss">
@use "@/variables.scss" as *;

label, .form-check-title {
  font-weight: bold;
  color: $bgColour;
}
.form-check > label {
  font-weight:normal;
  color: black;
}

input::placeholder, textarea::placeholder, .disabled {
  color: $linkColour;
}

.new-item {
  text-align: center;
  max-width: 450px;
  margin: 0 auto;
}

.error {
  color: red;
  font-size: 0.75em;
  font-weight: bold;
  margin-top:10px
}

form button {
  margin: 3px;
}

.explanation {
  font-size: 0.8rem;
  font-style: italic;
  color: $linkColour;
}
</style>